import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import CampaignUserForm from "../../components/forms/campaigns/CampaignUserForm";
import { generateUniqueNumber } from "../../shared/utility";

export function CampaignUsersViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [campaignUsers, setCampaignUsers] = useState({ data: [] });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const getCampaignUsers = async function (campaignId) {
    setLoading(true);
    try {
      const endpoint = campaignId
        ? `campaignUsers?campaign_id=${campaignId}`
        : "campaignUsers";
      const callResult = await makeGetApiCall(endpoint);
      if (callResult.success) {
        setCampaignUsers({ data: callResult.data.data || [] });
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const createCampaignUser = async function (values) {
    const payload = { name: values.name, pin: values.pin };
    const callResult = await makePostApiCall("campaignUsers/create", payload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Campaign user added successfully",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      setTimeout(function () {
        getCampaignUsers();
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not create campaign user",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  const updateCampaignUser = async function (user, values, skipClose = false) {
    const payload = { id: user.id };
    if (values.name !== undefined && values.name !== "") {
      payload.name = values.name;
    }
    const callResult = await makePostApiCall("campaignUsers/update", payload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Campaign user updated successfully",
          SnackbarType.Success,
        ),
      );
      if (!skipClose) {
        setSlideOver({ childComponent: null, open: false });
        setTimeout(function () {
          getCampaignUsers();
        }, 1000);
      }
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update campaign user",
            SnackbarType.Error,
          ),
        );
      }
      return false;
    }
  };

  const updateCampaignUserPin = async function (user, pin, skipClose = false) {
    const payload = { id: user.id, pin };
    const callResult = await makePostApiCall(
      "campaignUsers/update/pin",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "PIN updated successfully",
          SnackbarType.Success,
        ),
      );
      if (!skipClose) {
        setSlideOver({ childComponent: null, open: false });
        setTimeout(function () {
          getCampaignUsers();
        }, 1000);
      }
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update PIN",
            SnackbarType.Error,
          ),
        );
      }
      return false;
    }
  };

  const openAddCampaignUserSlideOver = () => {
    setSlideOver({
      childComponent: (
        <CampaignUserForm
          isEdit={false}
          setOpen={setSlideOver}
          actionCall={createCampaignUser}
        />
      ),
      open: true,
    });
  };

  const handleEditCampaignUser = async function (user, values) {
    const hasName = values.name !== undefined && values.name !== "";
    const hasPin = values.pin && /^\d{6}$/.test(values.pin);

    if (!hasName && !hasPin) {
      setSlideOver({ childComponent: null, open: false });
      return;
    }

    const skipClose = hasName && hasPin;
    if (hasName) {
      await updateCampaignUser(user, values, skipClose);
    }
    if (hasPin) {
      await updateCampaignUserPin(user, values.pin, skipClose);
    }
    if (skipClose) {
      setSlideOver({ childComponent: null, open: false });
      setTimeout(function () {
        getCampaignUsers();
      }, 1000);
    }
  };

  const openEditCampaignUserSlideOver = function (user) {
    setSlideOver({
      childComponent: (
        <CampaignUserForm
          user={user}
          isEdit={true}
          setOpen={setSlideOver}
          actionCall={(values) => handleEditCampaignUser(user, values)}
        />
      ),
      open: true,
    });
  };

  const deleteCampaignUser = async function (id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall("campaignUsers/delete", { id });
      if (callResult.success) {
        setEmptyModelOpen({
          open: false,
          childComponent: null,
        });
        getCampaignUsers();
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            "Campaign user deleted successfully",
            SnackbarType.Success,
          ),
        );
      } else if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not delete campaign user",
            SnackbarType.Error,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const askDeleteCampaignUser = function (user) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete Campaign User"
          buttonMessage="Delete"
          confirmAction={() => deleteCampaignUser(user.id)}
          message={`Are you sure you want to delete ${user.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteCampaignUser,
    campaignUsers,
    emptyModal,
    getCampaignUsers,
    loading,
    openAddCampaignUserSlideOver,
    openEditCampaignUserSlideOver,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
  };
}
